<script setup lang="ts">
import { computed, inject } from 'vue'
import { websiteEditableContextKey } from './editable-context'
import { resolveWebsiteLink } from './links'
import type { WebsiteLink } from './types'

const props = defineProps<{ link: WebsiteLink }>()
const editor = inject(websiteEditableContextKey, null)
const editable = computed(() => editor?.editable.value === true)
const resolved = computed(() => resolveWebsiteLink(props.link))

function selectLink(event: MouseEvent | KeyboardEvent): void {
  if (!editable.value) return

  event.preventDefault()
  event.stopPropagation()

  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  editor?.linkSelected({
    link: props.link,
    anchor: {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    },
  })
}

function selectLinkWithKeyboard(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') return

  selectLink(event)
}
</script>

<template>
  <component
    :is="editable ? 'span' : 'a'"
    :href="editable ? undefined : resolved.href"
    :target="editable ? undefined : resolved.target"
    :rel="editable ? undefined : resolved.rel"
    :data-site-link="editable || undefined"
    :role="editable ? 'button' : undefined"
    :tabindex="editable ? 0 : undefined"
    @click="selectLink"
    @keydown="selectLinkWithKeyboard"
  >
    <slot>{{ link.label }}</slot>
  </component>
</template>
